import { cloneRgba, rgbaEquals, type Rgba } from '../color.ts';
import { clampDepth } from './document.ts';
import { DEFAULT_WATER_COLOR, rgbaFromToken } from './palette.ts';
import {
  clearRingEdgeBendById,
  findRingAtPoint,
  findRingBend,
  findRingById,
  findRingEdge,
  findRingVertex,
  makeRingVertices,
  moveRingBendById,
  moveRingVertexById,
  removeRingById,
  removeRingVertexById,
  setRingColorById,
  setRingCornerRadiusById,
  setRingEdgeBendById,
} from './ringObjects.ts';
import {
  DEFAULT_CORNER_RADIUS,
  DEFAULT_WATER_DEPTH,
  MIN_RING_VERTICES,
  type MapDocument,
  type ShallowWaterZone,
  type Vec2,
  type WaterMaterial,
} from './types.ts';

const MATERIAL_COLORS: Record<WaterMaterial, Rgba> = {
  sand: rgbaFromToken('shallow-sand'),
  stone: rgbaFromToken('shallow-stone'),
  reef: rgbaFromToken('reef'),
};

const MATERIAL_DEPTHS: Record<WaterMaterial, number> = {
  sand: 0,
  stone: 2,
  reef: DEFAULT_WATER_DEPTH,
};

/** Picking a material suggests its matching swatch; the author can override it afterwards. */
export function defaultColorForMaterial(material: WaterMaterial): Rgba {
  return cloneRgba(MATERIAL_COLORS[material] ?? DEFAULT_WATER_COLOR);
}

/** Suggested depth when drawing a new zone of this material. */
export function defaultDepthForMaterial(material: WaterMaterial): number {
  return MATERIAL_DEPTHS[material] ?? 0;
}

export function addShallowWaterZone(
  map: MapDocument,
  vertices: readonly Vec2[],
  material: WaterMaterial,
  depth = defaultDepthForMaterial(material),
): ShallowWaterZone {
  if (vertices.length < MIN_RING_VERTICES) {
    throw new Error(`A shallow-water zone needs at least ${MIN_RING_VERTICES} vertices`);
  }

  const zone: ShallowWaterZone = {
    id: `water-${map.nextShallowWaterId}`,
    material,
    color: cloneRgba(defaultColorForMaterial(material)),
    depth: clampDepth(depth),
    vertices: makeRingVertices(map.bounds, vertices),
    edges: vertices.map(() => ({ bend: null })),
    cornerRadius: DEFAULT_CORNER_RADIUS,
  };

  map.nextShallowWaterId += 1;
  map.shallowWater.push(zone);
  return zone;
}

export function findShallowWater(map: MapDocument, zoneId: string): ShallowWaterZone | undefined {
  return findRingById(map.shallowWater, zoneId);
}

export function findShallowWaterAt(map: MapDocument, point: Vec2): ShallowWaterZone | undefined {
  return findRingAtPoint(map.shallowWater, point);
}

export function findShallowWaterVertexAt(
  map: MapDocument,
  point: Vec2,
  radius: number,
): { zoneId: string; index: number } | null {
  const found = findRingVertex(map.shallowWater, point, radius);
  return found ? { zoneId: found.id, index: found.index } : null;
}

export function moveShallowWaterVertex(
  map: MapDocument,
  zoneId: string,
  index: number,
  point: Vec2,
): boolean {
  return moveRingVertexById(map.shallowWater, map.bounds, zoneId, index, point);
}

export function removeShallowWaterVertex(map: MapDocument, zoneId: string, index: number): boolean {
  return removeRingVertexById(map.shallowWater, zoneId, index);
}

export function removeShallowWaterZone(map: MapDocument, zoneId: string): boolean {
  return removeRingById(map.shallowWater, zoneId);
}

export function setShallowWaterCornerRadius(
  map: MapDocument,
  zoneId: string,
  radius: number,
): boolean {
  return setRingCornerRadiusById(map.shallowWater, zoneId, radius);
}

export function setShallowWaterMaterial(
  map: MapDocument,
  zoneId: string,
  material: WaterMaterial,
): boolean {
  const zone = findShallowWater(map, zoneId);

  if (!zone) {
    return false;
  }

  // An untouched colour follows the material; a deliberately chosen one is left alone.
  if (rgbaEquals(zone.color, defaultColorForMaterial(zone.material))) {
    zone.color = defaultColorForMaterial(material);
  }

  zone.material = material;
  return true;
}

export function setShallowWaterColor(map: MapDocument, zoneId: string, color: Rgba): boolean {
  return setRingColorById(map.shallowWater, zoneId, color);
}

export function setShallowWaterDepth(map: MapDocument, zoneId: string, depth: number): boolean {
  const zone = findShallowWater(map, zoneId);

  if (!zone) {
    return false;
  }

  zone.depth = clampDepth(depth);
  return true;
}

export function findShallowWaterEdgeAt(
  map: MapDocument,
  zoneId: string,
  point: Vec2,
  radius: number,
): number | null {
  return findRingEdge(map.shallowWater, zoneId, point, radius);
}

export function findShallowWaterBendAt(
  map: MapDocument,
  zoneId: string,
  point: Vec2,
  radius: number,
): number | null {
  return findRingBend(map.shallowWater, zoneId, point, radius);
}

export function setShallowWaterEdgeBend(
  map: MapDocument,
  zoneId: string,
  edgeIndex: number,
  point: Vec2,
): boolean {
  return setRingEdgeBendById(map.shallowWater, map.bounds, zoneId, edgeIndex, point);
}

export function moveShallowWaterBend(
  map: MapDocument,
  zoneId: string,
  edgeIndex: number,
  point: Vec2,
): boolean {
  return moveRingBendById(map.shallowWater, map.bounds, zoneId, edgeIndex, point);
}

export function clearShallowWaterEdgeBend(map: MapDocument, zoneId: string, edgeIndex: number): boolean {
  return clearRingEdgeBendById(map.shallowWater, zoneId, edgeIndex);
}
